import { FC, useEffect, useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { getPopupPrefs, setPopupPrefs, subscribePopupPrefs, type PopupPrefs } from './messagePopupState';

type Permission = 'default' | 'granted' | 'denied' | 'unsupported';
const readPermission = (): Permission => ('Notification' in window ? Notification.permission : 'unsupported');

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const IS_MAC = /Mac/i.test(ua);
const IS_WINDOWS = /Windows/i.test(ua);
const BROWSER = /Edg\//.test(ua)
    ? 'Microsoft Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Google Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'your browser';

// Step 2 lives in the operating system, which a web page can neither read nor
// change — so it is spelled out for the user's own machine.
const OS_STEP = IS_MAC
    ? `System Settings → Notifications → ${BROWSER} → turn on "Allow notifications", style "Banners".`
    : IS_WINDOWS
      ? `Settings → System → Notifications → make sure ${BROWSER} is On, and Do not disturb is off.`
      : `Allow notifications for ${BROWSER} in your system settings.`;

const BROWSER_STEP: Record<Permission, string> = {
    granted: `${BROWSER} is allowed to show Taytan alerts.`,
    default: 'Not set up yet — click here, then choose "Allow".',
    denied: 'Blocked. Click the icon left of the address, open Notifications, choose "Allow", then refresh.',
    unsupported: 'This browser cannot show desktop notifications.'
};

const hintSx = { whiteSpace: 'normal', fontSize: 12, lineHeight: 1.4 } as const;

/**
 * Header button for new-message alerts: pop-ups, sound, and the two
 * permissions desktop notifications need (browser → site, OS → browser).
 * A dot on the icon flags that desktop notifications are not set up.
 */
const MessagePopupSettings: FC = () => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [prefs, setPrefs] = useState<PopupPrefs>(getPopupPrefs);
    const [permission, setPermission] = useState<Permission>(readPermission);

    useEffect(() => subscribePopupPrefs(setPrefs), []);

    // Keep the dot honest when the user changes the permission in the
    // browser's site settings.
    useEffect(() => {
        let status: PermissionStatus | null = null;
        navigator.permissions
            ?.query({ name: 'notifications' })
            .then((s) => {
                status = s;
                s.onchange = () => setPermission(readPermission());
            })
            .catch(() => {});
        return () => {
            if (status) status.onchange = null;
        };
    }, []);

    const requestPermission = () => {
        if (permission !== 'default') return;
        Notification.requestPermission()
            .then(setPermission)
            .catch(() => {});
    };

    const needsSetup = !prefs.popupsMuted && (permission === 'default' || permission === 'denied');

    return (
        <>
            <IconButton
                size="small"
                title={
                    needsSetup ? 'Message alerts — desktop notifications are not set up' : 'Message alerts'
                }
                sx={{ color: 'inherit' }}
                onClick={(e) => {
                    setPermission(readPermission());
                    setAnchorEl(e.currentTarget);
                }}
            >
                <Badge color="warning" variant="dot" invisible={!needsSetup}>
                    {prefs.popupsMuted ? (
                        <NotificationsOffOutlinedIcon fontSize="small" />
                    ) : (
                        <NotificationsActiveOutlinedIcon fontSize="small" />
                    )}
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                slotProps={{ paper: { sx: { width: 340, maxWidth: 'calc(100vw - 32px)' } } }}
            >
                <Box sx={{ px: 2, pt: 1, pb: 1 }}>
                    <Typography fontWeight={700} fontSize={14}>
                        New message alerts
                    </Typography>
                    <Typography color="text.secondary" sx={hintSx}>
                        A pop-up when a chat message arrives — on screen while you work in Taytan, and as a
                        desktop notification when you are in another window.
                    </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={() => setPopupPrefs({ popupsMuted: !prefs.popupsMuted })}>
                    <ListItemIcon>
                        <NotificationsActiveOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Pop-up for new messages" />
                    <Switch size="small" edge="end" checked={!prefs.popupsMuted} />
                </MenuItem>
                <MenuItem
                    disabled={prefs.popupsMuted}
                    onClick={() => setPopupPrefs({ soundMuted: !prefs.soundMuted })}
                >
                    <ListItemIcon>
                        <VolumeUpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Sound" />
                    <Switch size="small" edge="end" checked={!prefs.soundMuted} />
                </MenuItem>

                <Divider />
                <Box sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
                    <Typography fontWeight={700} fontSize={12} color="text.secondary">
                        DESKTOP NOTIFICATIONS — TWO SWITCHES
                    </Typography>
                </Box>
                <MenuItem
                    disabled={prefs.popupsMuted || permission !== 'default'}
                    onClick={requestPermission}
                >
                    <ListItemIcon>
                        {permission === 'granted' ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                            <ErrorOutlineIcon fontSize="small" color="warning" />
                        )}
                    </ListItemIcon>
                    <ListItemText
                        primary={`1. Allow in ${BROWSER}`}
                        secondary={BROWSER_STEP[permission]}
                        secondaryTypographyProps={{ sx: hintSx }}
                    />
                </MenuItem>
                <MenuItem disabled={prefs.popupsMuted} sx={{ cursor: 'default' }} disableRipple>
                    <ListItemIcon>
                        <DesktopWindowsOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary={`2. Allow ${BROWSER} in ${IS_MAC ? 'macOS' : IS_WINDOWS ? 'Windows' : 'your system'}`}
                        secondary={OS_STEP}
                        secondaryTypographyProps={{ sx: hintSx }}
                    />
                </MenuItem>
                <Box sx={{ display: 'flex', gap: 1, px: 2, py: 0.5, color: 'text.secondary' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, mt: '1px' }} />
                    <Typography sx={hintSx}>Taytan can check switch 1 but not switch 2.</Typography>
                </Box>
            </Menu>
        </>
    );
};

export default MessagePopupSettings;
